import { getData as getCandidates } from "../models/CandidatesModel";

export const CANDIDATE_MODULE = {
  title: "Candidates",
  singularTitle: "Candidate",
  key: "candidates",
  route: "candidates",
  primaryCol: "_candidate_id",
};

export function isResumeConverted(resume) {
  return !!(
    resume?.converted_candidate_id ||
    resume?.converted_candidate_unique_id
  );
}

export function getResumeConversionInfo(resume) {
  if (!isResumeConverted(resume)) return null;

  return {
    candidateId: resume.converted_candidate_id,
    candidateUniqueId: resume.converted_candidate_unique_id,
    candidateName: String(resume.converted_candidate_name || "").trim(),
  };
}

export function normalizeResumePhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length >= 10) {
    return digits.slice(-10);
  }
  return digits;
}

export function extractBasicFieldsFromResumeText(text, fileName = "") {
  const content = String(text || "").trim();
  const email =
    content.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || "";
  const phoneMatch =
    content.match(/(?:\+91[\s-]?)?[6-9]\d{9}|\b[6-9]\d{9}\b/);
  const phone = phoneMatch ? normalizeResumePhone(phoneMatch[0]) : "";

  const lines = content
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const name =
    lines.find((line) => line.length > 2 && !line.includes("@")) ||
    String(fileName || "").replace(/\.[^.]+$/, "");

  return {
    name,
    email,
    phone,
    location: "",
    education: [],
    experience: [],
    skills: [],
  };
}

export function splitResumeName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { firstname: "", lastname: "" };
  }
  if (parts.length === 1) {
    return { firstname: parts[0], lastname: "" };
  }

  return {
    firstname: parts[0],
    lastname: parts.slice(1).join(" "),
  };
}

export function parseResumeExperiences(experiencesText) {
  if (!experiencesText) return [];

  return String(experiencesText)
    .split("||")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const [title, company, duration, location] = chunk
        .split("|")
        .map((part) => part.trim());
      return { title, company, duration, location };
    });
}

export function formatResumeEducation(educationRaw) {
  if (!educationRaw) return "";

  if (typeof educationRaw === "string") {
    const trimmed = educationRaw.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((row) =>
              [row.degree, row.institution, row.year, row.percentage]
                .filter(Boolean)
                .join(" — ")
            )
            .filter(Boolean)
            .join("; ");
        }
      } catch (error) {
        return trimmed;
      }
    }
    return trimmed;
  }

  return String(educationRaw);
}

export function matchSkillsFromResume(resumeSkillsText, skillOptions = []) {
  if (!resumeSkillsText || !skillOptions.length) return [];

  const tokens = String(resumeSkillsText)
    .split(/[,;|/]/)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  if (!tokens.length) return [];

  const matched = [];
  const seen = new Set();

  skillOptions.forEach((option) => {
    const label = String(option.label || "").trim().toLowerCase();
    if (!label) return;

    const isMatch = tokens.some(
      (token) =>
        label === token ||
        label.includes(token) ||
        token.includes(label)
    );

    if (isMatch && !seen.has(option.value)) {
      seen.add(option.value);
      matched.push({ value: option.value, label: option.label });
    }
  });

  return matched;
}

export function buildCandidatePrefillFromResume(resume, skillOptions = []) {
  if (!resume?._resume_id) return null;

  const { firstname, lastname } = splitResumeName(resume.resume_name);
  const experiences = parseResumeExperiences(resume.experiences);
  const latestExperience = experiences[0];
  const experienceSummary = experiences
    .slice(0, 3)
    .map((exp) =>
      [exp.title, exp.company, exp.duration].filter(Boolean).join(" @ ")
    )
    .filter(Boolean)
    .join("; ");

  return {
    resumeId: resume._resume_id,
    resumeName: resume.resume_name,
    candidateFields: {
      candidate_firstname: firstname,
      candidate_lastname: lastname,
      candidate_email: resume.resume_email || "",
      candidate_mobile: normalizeResumePhone(resume.resume_phone),
      candidate_present_location: resume.resume_location || "",
      candidate_preffered_location: resume.resume_location || "",
      candidate_education: formatResumeEducation(resume.resume_education),
      candidate_experience_total: "",
      candidate_experience_relevant: "",
      candidate_current_company: latestExperience?.company || "",
      candidate_remarks: experienceSummary
        ? `From resume: ${experienceSummary}`
        : "",
      candidate_skills: matchSkillsFromResume(resume.resume_skills, skillOptions),
      candidate_resume_upload_id: resume._resume_id,
      candidate_resume_id: resume.resume_file_id || "",
    },
  };
}

export function buildCandidateCreateStateFromResume(
  resume,
  { clientId, clientName, requirementId, requirementName },
  skillOptions = []
) {
  const resumePrefill = buildCandidatePrefillFromResume(resume, skillOptions);
  if (!resumePrefill) return null;

  return {
    module: CANDIDATE_MODULE,
    prefill: {
      fromResume: true,
      resumeId: resumePrefill.resumeId,
      resumeName: resumePrefill.resumeName,
      resumeFileId: resume.resume_file_id || "",
      resumeFileName: resume.resume_file_name || "",
      clientId,
      clientName,
      requirementId,
      requirementName,
      candidateFields: resumePrefill.candidateFields,
    },
  };
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "");
}

export async function findDuplicateCandidates(userToken, { email, mobile } = {}) {
  if (!userToken) return [];

  const normalizedEmail = normalizeEmail(email);
  const normalizedMobile = normalizePhone(mobile);

  if (!normalizedEmail && !normalizedMobile) return [];

  try {
    const response = await getCandidates({
      user_token: userToken,
      page: 1,
      limit: -1,
      include_inactive_mappings: 1,
    });

    return (response?.candidates || []).filter((row) => {
      if (
        normalizedEmail &&
        normalizeEmail(row.candidate_email) === normalizedEmail
      ) {
        return true;
      }
      if (
        normalizedMobile &&
        normalizePhone(row.candidate_mobile) === normalizedMobile
      ) {
        return true;
      }
      return false;
    });
  } catch (error) {
    console.error(error);
    return [];
  }
}

export function formatDuplicateCandidateLabel(candidate) {
  if (!candidate) return "Existing candidate";
  const name = `${candidate.candidate_firstname || ""} ${candidate.candidate_lastname || ""}`.trim();
  const contact = candidate.candidate_email || candidate.candidate_mobile || "";
  return [name || "Unnamed", contact].filter(Boolean).join(" — ");
}
